import React, { useLayoutEffect, useRef, useState, useMemo } from "react";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import { fmtNum } from "../../utils/formatUtils";

/* ── types ────────────────────────────────────────────────────── */
export interface FundingRow {
  asset_class: string;
  plus10_funding_100_strategic: number;
  plus10_funding_30_strategic: number;
  minus10_funding_100_strategic: number;
  minus10_funding_30_strategic: number;
}

export interface RawFundingRow {
  asset_class?: string;
  scenario?: string;
  funding_100_strategic?: number | string;
  funding_30_strategic?: number | string;
}

export interface StressRow {
  scenario: string;
  total_margin_call: number;
  new_margin_call: number;
  pending_flows: number;
  exchange_vm: number;
}

export interface GridProps {
  rows: any[];
  title: string;
}

/* ── helpers ──────────────────────────────────────────────────── */
function isPivotFundingRow(r: any): boolean {
  if (!r) return false;
  return (
    Object.prototype.hasOwnProperty.call(r, "plus10_funding_100_strategic") ||
    Object.prototype.hasOwnProperty.call(r, "plus10_funding_30_strategic") ||
    Object.prototype.hasOwnProperty.call(r, "minus10_funding_100_strategic") ||
    Object.prototype.hasOwnProperty.call(r, "minus10_funding_30_strategic")
  );
}

function mapScenarioToShock(scenario: string | undefined): string | null {
  if (!scenario) return null;
  if (scenario.endsWith("+")) return "plus10";
  if (scenario.endsWith("-")) return "minus10";
  return null;
}

export function pivotFundingIfNeeded(rows: any[]): FundingRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (isPivotFundingRow(rows[0])) return rows;
  const byAsset = new Map<string, FundingRow>();
  for (const r of rows as RawFundingRow[]) {
    const asset_class = r.asset_class || "";
    const shockKey = mapScenarioToShock(r.scenario);
    if (!asset_class || !shockKey) continue;
    if (!byAsset.has(asset_class))
      byAsset.set(asset_class, {
        asset_class,
        plus10_funding_100_strategic: 0,
        plus10_funding_30_strategic: 0,
        minus10_funding_100_strategic: 0,
        minus10_funding_30_strategic: 0,
      });
    const acc = byAsset.get(asset_class)!;
    const f100 = Number(r.funding_100_strategic || 0);
    const f30 = Number(r.funding_30_strategic || 0);
    if (shockKey === "plus10") {
      acc.plus10_funding_100_strategic += f100;
      acc.plus10_funding_30_strategic += f30;
    } else {
      acc.minus10_funding_100_strategic += f100;
      acc.minus10_funding_30_strategic += f30;
    }
  }
  const order = ["All Asset Classes", "Carbon (Compliance)", "Power + Gas", "Ags + Softs + Bio + Feedstock", "Oil + Petrochemical"];
  const ordered: FundingRow[] = [];
  for (const k of order) if (byAsset.has(k)) ordered.push(byAsset.get(k)!);
  for (const [k, v] of byAsset.entries()) if (!order.includes(k)) ordered.push(v);
  return ordered;
}

function splitHeaderRender(line1: string, line2: string, line3?: string): () => React.ReactElement {
  return () => (
    <div style={{ textAlign: "right", lineHeight: 1.25 }}>
      <div style={{ fontSize: "12px", fontWeight: 700 }}>{line1}</div>
      <div style={{ fontSize: "12px", fontWeight: 700 }}>{line2}</div>
      <div style={{ fontSize: "11px", opacity: 0.8 }}>{line3}</div>
    </div>
  );
}

function numericCellRender(cellData: any): React.ReactElement {
  const n = Number(cellData?.value);
  const cls = !Number.isFinite(n) ? "" : n < 0 ? "neg" : n > 0 ? "pos" : "";
  return <span className={`num ${cls}`}>{fmtNum(cellData?.value)}</span>;
}

export function onCellPrepared(e: any): void {
  if (!e.column?.fixed) return;
  const isHeader = e.rowType === "header";
  const isAlt = e.rowType === "data" && e.rowIndex % 2 !== 0;
  e.cellElement.style.background = isHeader ? "#0a2540" : isAlt ? "#112540" : "#0e2035";
  e.cellElement.style.color = isHeader ? "#c9ddf5" : "#dce8f5";
  e.cellElement.style.borderColor = isHeader ? "#1a4870" : "rgba(255,255,255,0.1)";
}

function useColWidth(fixedWidth: number, numCols: number, minColWidth: number): [React.RefObject<HTMLDivElement | null>, number] {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [colWidth, setColWidth] = useState<number>(minColWidth);
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const total = el.offsetWidth;
      const scrollable = total - fixedWidth - 44;
      const w = Math.max(Math.floor(scrollable / numCols), minColWidth);
      setColWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedWidth, numCols, minColWidth]);
  return [containerRef, colWidth];
}

/* ── grid components ─────────────────────────────────────────── */
export function GridFundingSensitivity({ rows, title }: GridProps): React.ReactElement {
  const dataSource = useMemo(() => pivotFundingIfNeeded(rows), [rows]);
  const FIXED = 140, COLS = 4, MIN = 90;
  const [containerRef, colWidth] = useColWidth(FIXED, COLS, MIN);
  return (
    <div className="card" ref={containerRef}>
      <div className="cardTitle">{title}</div>
      <DataGrid className="dx-compact-grid" dataSource={dataSource}
        showBorders rowAlternationEnabled height={42 + dataSource.length * 33} onCellPrepared={onCellPrepared}
        width="100%" columnAutoWidth={false}>
        <Paging enabled={false} />
        <Column dataField="asset_class" caption="Asset Class" fixed={true} fixedPosition="left" width={FIXED} />
        <Column dataField="plus10_funding_100_strategic" headerCellRender={splitHeaderRender("+10% Funding", "Sensitivity", "(100% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
        <Column dataField="plus10_funding_30_strategic" headerCellRender={splitHeaderRender("+10% Funding", "Sensitivity", "(30% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
        <Column dataField="minus10_funding_100_strategic" headerCellRender={splitHeaderRender("-10% Funding", "Sensitivity", "(100% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
        <Column dataField="minus10_funding_30_strategic" headerCellRender={splitHeaderRender("-10% Funding", "Sensitivity", "(30% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
      </DataGrid>
      <div className="footnote">Positive numbers = Cash in for CFP; Negative numbers = Cash out for CFP</div>
    </div>
  );
}

export function GridStressScenarios({ rows, title }: GridProps): React.ReactElement {
  const FIXED = 130, COLS = 4, MIN = 85;
  const [containerRef, colWidth] = useColWidth(FIXED, COLS, MIN);
  return (
    <div className="card" ref={containerRef}>
      <div className="cardTitle">{title}</div>
      <DataGrid className="dx-compact-grid" dataSource={rows ?? []}
        showBorders rowAlternationEnabled height={42 + (rows ?? []).length * 33} onCellPrepared={onCellPrepared}
        width="100%" columnAutoWidth={false}>
        <Paging enabled={false} />
        <Column dataField="scenario" caption="Scenario" fixed={true} fixedPosition="left" width={FIXED} />
        <Column dataField="total_margin_call" headerCellRender={splitHeaderRender("Total Margin Call", "(30% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
        <Column dataField="new_margin_call" headerCellRender={splitHeaderRender("New Margin Call", "(30% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
        <Column dataField="pending_flows" caption="Pending Flows" alignment="right" cellRender={(c: any) => <span className="num">{fmtNum(c?.value)}</span>} width={colWidth} />
        <Column dataField="exchange_vm" headerCellRender={splitHeaderRender("Exchange VM", "(30% Strategic)")} alignment="right" cellRender={numericCellRender} width={colWidth} />
      </DataGrid>
      <div className="footnote">Positive numbers = Cash in for CFP; Negative numbers = Cash out for CFP</div>
    </div>
  );
}
